import type { MyCompetition } from '@/api/types';
import { CompetitionStatus } from '@/api/types';

/** Human-readable (Slovenian) label for a numeric CompetitionStatus ordinal. */
export const COMPETITION_STATUS_LABELS: Record<CompetitionStatus, string> = {
  [CompetitionStatus.Draft]: 'Osnutek',
  [CompetitionStatus.Scheduled]: 'Načrtovano',
  [CompetitionStatus.Active]: 'Aktivno',
  [CompetitionStatus.Review]: 'Pregled',
  [CompetitionStatus.Judging]: 'Ocenjevanje',
  [CompetitionStatus.Awarding]: 'Podeljevanje nagrad',
  [CompetitionStatus.Completed]: 'Zaključeno',
  [CompetitionStatus.Archived]: 'Arhivirano',
};

export function competitionStatusLabel(status: CompetitionStatus): string {
  return COMPETITION_STATUS_LABELS[status] ?? `Neznano (${status})`;
}

/**
 * Stable English key for Badge's color lookup (components/ui/Badge.tsx's
 * colorMap) - kept separate from the translated display label above, since
 * the label changes with language but the badge's tone/color must not.
 */
export const COMPETITION_STATUS_TONES: Record<CompetitionStatus, string> = {
  [CompetitionStatus.Draft]: 'Draft',
  [CompetitionStatus.Scheduled]: 'Scheduled',
  [CompetitionStatus.Active]: 'Active',
  [CompetitionStatus.Review]: 'UnderReview',
  [CompetitionStatus.Judging]: 'Judging',
  [CompetitionStatus.Awarding]: 'Awarding',
  [CompetitionStatus.Completed]: 'Completed',
  [CompetitionStatus.Archived]: 'Archived',
};

export function competitionStatusTone(status: CompetitionStatus): string {
  return COMPETITION_STATUS_TONES[status] ?? 'Draft';
}

/** All statuses, in lifecycle order, for building filter dropdowns etc. */
export const COMPETITION_STATUS_OPTIONS: CompetitionStatus[] = [
  CompetitionStatus.Draft,
  CompetitionStatus.Scheduled,
  CompetitionStatus.Active,
  CompetitionStatus.Review,
  CompetitionStatus.Judging,
  CompetitionStatus.Awarding,
  CompetitionStatus.Completed,
  CompetitionStatus.Archived,
];

export type CompetitionLifecycleAction =
  | 'schedule'
  | 'activate'
  | 'startReview'
  | 'startJudging'
  | 'startAwarding'
  | 'complete'
  | 'archive'
  | 'revertToReview'
  | 'revertToJudging'
  | 'reopenAwarding'
  | 'revertToActive';

/**
 * Mirrors Competition.AllowedTransitions in
 * backend/src/Aperture.Domain/Competitions/Competition.cs - the only
 * transitions that won't 400. (Scheduled -> Draft / RevertToDraft is
 * intentionally omitted: there's no controller endpoint exposed for it.)
 * Review closes submissions so the organizer can reject entries (the rest are accepted when
 * judging starts); judging can only start from there. Judging can reopen the review (decide a
 * late entry) and Review/Judging/Completed can revert back to Active. After judging comes
 * Awarding (selection for the exhibition + awards); results are published by completing it.
 */
export const COMPETITION_LIFECYCLE_ACTIONS: Record<
  CompetitionStatus,
  { action: CompetitionLifecycleAction; label: string }[]
> = {
  [CompetitionStatus.Draft]: [{ action: 'schedule', label: 'Načrtuj' }],
  [CompetitionStatus.Scheduled]: [{ action: 'activate', label: 'Aktiviraj' }],
  [CompetitionStatus.Active]: [{ action: 'startReview', label: 'Začni pregled' }],
  [CompetitionStatus.Review]: [
    { action: 'startJudging', label: 'Začni ocenjevanje' },
    { action: 'revertToActive', label: 'Vrni na aktivno' },
  ],
  [CompetitionStatus.Judging]: [
    { action: 'startAwarding', label: 'Zaključi ocenjevanje' },
    { action: 'revertToReview', label: 'Vrni na pregled' },
    { action: 'revertToActive', label: 'Vrni na aktivno' },
  ],
  [CompetitionStatus.Awarding]: [
    { action: 'complete', label: 'Objavi rezultate' },
    { action: 'revertToJudging', label: 'Vrni na ocenjevanje' },
  ],
  [CompetitionStatus.Completed]: [
    { action: 'archive', label: 'Arhiviraj' },
    { action: 'reopenAwarding', label: 'Vrni na podeljevanje nagrad' },
    { action: 'revertToActive', label: 'Vrni na aktivno' },
  ],
  [CompetitionStatus.Archived]: [],
};

/** True once a competition has moved past Draft/Scheduled and can no longer have its name/dates edited (see Competition.UpdateDetails guard). */
export function isCompetitionEditable(status: CompetitionStatus): boolean {
  return status !== CompetitionStatus.Completed && status !== CompetitionStatus.Archived;
}

/** Label for the tag shown when a competition is held under the patronage of FZS (Fotografska zveza Slovenije). */
export const FZS_PATRONAGE_LABEL = 'Pod pokroviteljstvom FZS';

/** A competition shows up for its organizers, judges and competitors - nobody else. */
export function isMine(m: MyCompetition): boolean {
  return m.canManage || m.isJudge || m.isParticipant;
}
